import { Group, Rule, TypeId } from '@wellesley/openapi'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { rootReducer } from '@wellesley/store'
import {
  setGroupData,
  setGroupLoading,
} from '@wellesley/host-remote/api/actions'

export type GroupState = {
  data: Record<
    string,
    {
      data: Group | null
      loading: boolean
    }
  >
  ui: GroupUIState
  localState: GroupLocalState
}

type GroupLocalState = {
  description: string
  rules: Rule[] | null
  locations: string[] | null
}

type GroupUIState = {
  showInfo: boolean
}

type UpdateGroupMediaPayload = {
  groupName: string
  uploadId: string
}

const initialState: GroupState = {
  data: {},
  ui: {
    showInfo: false,
  },
  localState: {
    description: '',
    rules: null,
    locations: null,
  },
}

const initialGroupState = {
  data: null,
  loading: false,
  ui: {
    showInfo: true,
  },
}

export const groupSlice = createSlice({
  name: 'group',
  initialState,
  reducers: {
    setDescription: (state, { payload }: PayloadAction<string>) => {
      state.localState.description = payload
    },
    setRules: (state, { payload }: PayloadAction<Rule[]>) => {
      state.localState.rules = payload
    },
    addRule: (state, { payload }: PayloadAction<Rule>) => {
      state.localState.rules = [...(state.localState.rules || []), payload]
    },
    updateRule: (state, { payload }: PayloadAction<Rule>) => {
      state.localState.rules = (state.localState.rules || []).map((rule) =>
        rule.id === payload.id ? payload : rule,
      )
    },
    removeRule: (state, { payload }: PayloadAction<string>) => {
      state.localState.rules = (state.localState.rules || []).filter(
        (rule) => rule.id !== payload,
      )
    },
    reorderRules: (state, { payload }: PayloadAction<Rule[]>) => {
      state.localState.rules = payload
    },
    setLocations: (state, { payload }: PayloadAction<string[]>) => {
      state.localState.locations = payload
    },
    removeLocation: (state, { payload }: PayloadAction<string>) => {
      state.localState.locations = (state.localState.locations || []).filter(
        (location) => location !== payload,
      )
    },
    updateGroupBanner: (
      state,
      { payload }: PayloadAction<UpdateGroupMediaPayload>,
    ) => {
      const entry = state.data[payload.groupName]
      if (entry?.data?.banner) {
        entry.data.banner.uploadId = payload.uploadId
      }
    },
    updateGroupAvatar: (
      state,
      { payload }: PayloadAction<UpdateGroupMediaPayload>,
    ) => {
      const entry = state.data[payload.groupName]
      if (entry?.data?.avatar) {
        entry.data.avatar.uploadId = payload.uploadId
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setGroupData, (state, { payload }) => {
        const [groupId, group] = payload
        if (!state.data[groupId]) {
          state.data[groupId] = { ...initialGroupState }
        }
        state.data[groupId].data = group
      })
      .addCase(setGroupLoading, (state, { payload }) => {
        const [groupId, isLoading] = payload
        if (!state.data[groupId]) {
          state.data[groupId] = { ...initialGroupState }
        }
        state.data[groupId].loading = isLoading
      })
  },
  selectors: {
    group: (state: GroupState, groupId: TypeId) => state.data[groupId]?.data,
    isLoading: (state: GroupState, groupId: TypeId) =>
      state.data[groupId]?.loading,
    showBasicInfo: (state: GroupState) => state.ui.showInfo,
    selectDescription: (state: GroupState) => state.localState.description,
    selectRules: (state: GroupState) => state.localState.rules,
    selectLocations: (state: GroupState) => state.localState.locations,
  },
}).injectInto(rootReducer)
